import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import {
  Calendar, Users, UtensilsCrossed, Sparkles, Music, MapPin, Phone,
  Mail, ChevronRight, ChevronLeft, Send, AlertCircle, Check, Building2
} from "lucide-react";

const fmt = (n) => `$${Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 2 })}`;

const STORAGE_KEY = "cotizador_publico_state_v1";

const CotizadorPublico = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [calc, setCalc] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [calcError, setCalcError] = useState(null);
  const [sending, setSending] = useState(false);

  // ── Persistencia local (sobrevive refrescos / navegación) ──
  const initialStored = (() => {
    try {
      const raw = typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) : null;
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();

  const [step, setStep] = useState(initialStored?.step && initialStored.step >= 1 && initialStored.step <= 4 ? initialStored.step : 1);

  const [form, setForm] = useState(initialStored?.form || {
    nombre: "", telefono: "", correo: "", whatsapp: "",
    nombre_festejado: "", tipo_evento: "Evento social",
    fecha_evento: "", horario: "",
    adultos: 0, ninos: 0, jovenes: 0, bebes: 0,
    menu_adulto_id: "", menu_nino_id: "",
    tipo_cotizacion: "instalaciones_mas_menu",
    incluye_instalaciones: true,
    incluye_dj: false,
    horas_extra_dj: 0,
    horas_extra_local: 0,
    extras: [],
    observaciones: "",
  });

  // Persistir cambios de step y form
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ step, form }));
    } catch { /* storage lleno o denegado */ }
  }, [step, form]);

  // Iter97 · SaaS: leer ?e=empresa_id de la URL para multi-tenant.
  const empresaId = useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).get("e") || "";
    } catch { return ""; }
  }, []);

  useEffect(() => {
    const url = empresaId ? `${API}/cotizador/config?empresa_id=${encodeURIComponent(empresaId)}` : `${API}/cotizador/config`;
    axios.get(url).then(r => {
      setConfig(r.data);
      // Preselecciona menu principal si existe (SOLO si no había persistencia previa)
      const principal = r.data.menus.find(m => m.es_menu_principal) || r.data.menus.find(m => m.categoria === "adulto");
      const infantil = r.data.menus.find(m => m.categoria === "infantil");
      const tipoDefault = r.data.tipo_cotizacion_default || "instalaciones_mas_menu";
      setForm(p => ({
        ...p,
        menu_adulto_id: p.menu_adulto_id || principal?.item_id || "",
        menu_nino_id: p.menu_nino_id || infantil?.item_id || "",
        tipo_cotizacion: p.tipo_cotizacion || tipoDefault,
        incluye_instalaciones: p.tipo_cotizacion ? p.incluye_instalaciones : tipoDefault !== "paquete_completo",
      }));
    }).catch(() => toast.error("Error al cargar el cotizador"));
  }, [empresaId]);

  const calcular = useCallback(async (nextForm) => {
    if (!config || !nextForm.menu_adulto_id) return;
    setCalculating(true);
    setCalcError(null);
    try {
      const { data } = await axios.post(`${API}/cotizador/calcular`, {
        empresa_id: empresaId || undefined,
        adultos: nextForm.adultos,
        ninos: nextForm.ninos,
        menu_adulto_id: nextForm.menu_adulto_id,
        menu_nino_id: nextForm.menu_nino_id,
        tipo_cotizacion: nextForm.tipo_cotizacion,
        incluye_instalaciones: nextForm.incluye_instalaciones,
        incluye_dj: nextForm.incluye_dj,
        horas_extra_dj: nextForm.horas_extra_dj,
        horas_extra_local: nextForm.horas_extra_local,
        extras: nextForm.extras,
        iva_activo: config.iva_activo,
      });
      setCalc(data);
    } catch (err) {
      setCalcError(err?.response?.data?.detail || "No se pudo calcular el resumen. Verifica los datos e inténtalo de nuevo.");
    } finally {
      setCalculating(false);
    }
  }, [config]);

  useEffect(() => {
    if (config) calcular(form);
  }, [form, config, calcular]);

  const update = (patch) => setForm(p => ({ ...p, ...patch }));

  const toggleExtra = (servicio_id, cantidad = 1) => {
    setForm(p => {
      const existing = p.extras.find(e => e.servicio_id === servicio_id);
      if (existing) return { ...p, extras: p.extras.filter(e => e.servicio_id !== servicio_id) };
      return { ...p, extras: [...p.extras, { servicio_id, cantidad }] };
    });
  };

  const updateExtraQty = (servicio_id, cantidad) => {
    setForm(p => ({
      ...p,
      extras: p.extras.map(e => e.servicio_id === servicio_id ? { ...e, cantidad } : e)
    }));
  };

  const isExtraSelected = (servicio_id) => form.extras.some(e => e.servicio_id === servicio_id);

  const enviar = async () => {
    if (!form.nombre.trim() || !form.telefono.trim()) {
      toast.error("Captura tu nombre y teléfono");
      setStep(1);
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/cotizador/enviar`, { ...form, empresa_id: empresaId || undefined });
      toast.success("Cotización enviada. Te contactaremos pronto.");
      // Limpiar persistencia local tras envío exitoso
      try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
      navigate(`/cotizacion/${data.public_token}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al enviar");
    } finally {
      setSending(false);
    }
  };

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const adultosMenus = config.menus.filter(m => m.categoria === "adulto");
  const infantilMenus = config.menus.filter(m => m.categoria === "infantil");
  const otherMenus = config.menus.filter(m => m.categoria !== "adulto" && m.categoria !== "infantil");

  return (
    <div className="min-h-screen bg-stone-50 pb-12" data-testid="cotizador-publico">
      <header className="bg-emerald-900 text-stone-50 py-8 px-4 text-center">
        <h1 className="font-display text-3xl sm:text-4xl">Cotizador Automático de Eventos</h1>
        <p className="text-sm sm:text-base text-stone-200 mt-2 max-w-md mx-auto" data-testid="cotizador-subtitulo">
          {config?.branding?.nombre_empresa
            ? (config?.branding?.subtitulo || config.branding.nombre_empresa)
            : "Cotiza tu evento en 4 pasos"}
        </p>
      </header>

      <div className="max-w-3xl mx-auto px-4 pt-6">
        {/* Stepper */}
        <div className="flex items-center justify-center gap-2 mb-6 overflow-x-auto">
          {[1, 2, 3, 4].map(s => (
            <div
              key={s}
              className={`flex items-center gap-2 ${step === s ? "text-emerald-900" : "text-stone-400"}`}
            >
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step === s ? "bg-emerald-900 text-white" : step > s ? "bg-emerald-200 text-emerald-900" : "bg-stone-200"}`}>
                {step > s ? <Check className="w-4 h-4" /> : s}
              </div>
              <span className="text-xs sm:text-sm whitespace-nowrap hidden sm:inline">
                {["Tus datos", "Personas y menús", "Servicios extras", "Resumen"][s - 1]}
              </span>
              {s < 4 && <div className={`w-6 h-px ${step > s ? "bg-emerald-300" : "bg-stone-200"} hidden sm:block`} />}
            </div>
          ))}
        </div>

        {/* STEP 1 - Datos cliente */}
        {step === 1 && (
          <Card className="border-stone-200">
            <CardContent className="p-5 space-y-4">
              <h2 className="font-display text-xl text-stone-900">Cuéntanos de ti y tu evento</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Nombre *</Label>
                  <Input value={form.nombre} onChange={(e) => update({ nombre: e.target.value })} className="border-stone-300" data-testid="input-nombre" />
                </div>
                <div>
                  <Label>Teléfono / WhatsApp *</Label>
                  <Input value={form.telefono} onChange={(e) => update({ telefono: e.target.value, whatsapp: e.target.value })} className="border-stone-300" data-testid="input-telefono" />
                </div>
                <div>
                  <Label>Correo</Label>
                  <Input type="email" value={form.correo} onChange={(e) => update({ correo: e.target.value })} className="border-stone-300" data-testid="input-correo" />
                </div>
                <div>
                  <Label>Nombre del festejado</Label>
                  <Input value={form.nombre_festejado} onChange={(e) => update({ nombre_festejado: e.target.value })} className="border-stone-300" data-testid="input-festejado" />
                </div>
                <div>
                  <Label>Tipo de evento</Label>
                  <Select value={form.tipo_evento} onValueChange={(v) => update({ tipo_evento: v })}>
                    <SelectTrigger className="border-stone-300" data-testid="select-tipo-evento"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {config.tipos_evento.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Fecha del evento</Label>
                  <Input type="date" value={form.fecha_evento} onChange={(e) => update({ fecha_evento: e.target.value })} className="border-stone-300" data-testid="input-fecha" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Horario</Label>
                  <Input value={form.horario} onChange={(e) => update({ horario: e.target.value })} placeholder="ej: 18:00 - 23:00" className="border-stone-300" data-testid="input-horario" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Observaciones</Label>
                  <Textarea rows={3} value={form.observaciones} onChange={(e) => update({ observaciones: e.target.value })} className="border-stone-300" data-testid="input-observaciones" />
                </div>

                {/* Demografía: adultos / jóvenes / niños / bebés */}
                <div className="sm:col-span-2 mt-2 p-3 rounded-lg border border-emerald-200 bg-emerald-50">
                  <Label className="text-emerald-900 font-semibold">¿Cuántas personas asistirán?</Label>
                  <p className="text-xs text-emerald-800 mb-2">Lo usamos para calcular menús y servicios.</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div>
                      <Label className="text-xs">Adultos</Label>
                      <Input type="number" min="0" value={form.adultos} onChange={(e) => update({ adultos: Number(e.target.value) || 0 })} className="border-stone-300" data-testid="input-adultos" />
                    </div>
                    <div>
                      <Label className="text-xs">Jóvenes</Label>
                      <Input type="number" min="0" value={form.jovenes} onChange={(e) => update({ jovenes: Number(e.target.value) || 0 })} className="border-stone-300" data-testid="input-jovenes" />
                    </div>
                    <div>
                      <Label className="text-xs">Niños</Label>
                      <Input type="number" min="0" value={form.ninos} onChange={(e) => update({ ninos: Number(e.target.value) || 0 })} className="border-stone-300" data-testid="input-ninos" />
                    </div>
                    <div>
                      <Label className="text-xs">Bebés</Label>
                      <Input type="number" min="0" value={form.bebes} onChange={(e) => update({ bebes: Number(e.target.value) || 0 })} className="border-stone-300" data-testid="input-bebes" />
                    </div>
                  </div>
                </div>
              </div>
              <Button onClick={() => setStep(2)} className="w-full bg-emerald-900 hover:bg-emerald-800 text-white h-12" data-testid="btn-step1-next">
                Continuar <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* STEP 2 - Personas y menus */}
        {step === 2 && (
          <Card className="border-stone-200">
            <CardContent className="p-5 space-y-4">
              <h2 className="font-display text-xl text-stone-900 flex items-center gap-2"><Users className="w-5 h-5 text-emerald-900" />Personas y menús</h2>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Adultos</Label>
                  <Input type="number" min={0} value={form.adultos} onChange={(e) => update({ adultos: parseInt(e.target.value) || 0 })} className="border-stone-300" data-testid="input-adultos" />
                </div>
                <div>
                  <Label>Niños</Label>
                  <Input type="number" min={0} value={form.ninos} onChange={(e) => update({ ninos: parseInt(e.target.value) || 0 })} className="border-stone-300" data-testid="input-ninos" />
                </div>
              </div>
              {calc?.nota_minimo && (
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-800 flex gap-2" data-testid="alert-minimo">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{calc.nota_minimo}</span>
                </div>
              )}
              {calc && (
                <div className="text-xs text-stone-500 bg-stone-50 p-2 rounded">
                  Personas registradas: <b>{calc.personas_registradas}</b> · Cobrables: <b>{calc.personas_cobrables}</b>
                  {calc.personas_faltantes > 0 && <> · Ajuste: <b>{calc.personas_faltantes} personas</b></>}
                </div>
              )}
              <div>
                <Label className="mb-1 block">Menú para adultos</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[...adultosMenus, ...otherMenus].map(m => (
                    <button
                      key={m.item_id}
                      type="button"
                      onClick={() => update({ menu_adulto_id: m.item_id })}
                      className={`text-left p-3 border rounded-lg transition-colors ${form.menu_adulto_id === m.item_id ? "border-emerald-700 bg-emerald-50" : "border-stone-200 hover:border-emerald-300"}`}
                      data-testid={`menu-adulto-${m.item_id}`}
                    >
                      <p className="font-medium text-stone-900">{m.nombre}</p>
                      <p className="text-xs text-stone-500">{m.descripcion}</p>
                      <p className="text-sm text-emerald-700 font-semibold mt-1">{fmt(m.precio)}/persona</p>
                    </button>
                  ))}
                </div>
              </div>
              {form.ninos > 0 && infantilMenus.length > 0 && (
                <div>
                  <Label className="mb-1 block">Menú infantil</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {infantilMenus.map(m => (
                      <button
                        key={m.item_id}
                        type="button"
                        onClick={() => update({ menu_nino_id: m.item_id })}
                        className={`text-left p-3 border rounded-lg transition-colors ${form.menu_nino_id === m.item_id ? "border-emerald-700 bg-emerald-50" : "border-stone-200 hover:border-emerald-300"}`}
                        data-testid={`menu-nino-${m.item_id}`}
                      >
                        <p className="font-medium text-stone-900">{m.nombre}</p>
                        <p className="text-sm text-emerald-700 font-semibold mt-1">{fmt(m.precio)}/niño</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <Button onClick={() => setStep(1)} variant="outline" className="flex-1 border-stone-300"><ChevronLeft className="w-4 h-4 mr-1" />Atrás</Button>
                <Button onClick={() => setStep(3)} className="flex-1 bg-emerald-900 hover:bg-emerald-800 text-white" data-testid="btn-step2-next">Continuar <ChevronRight className="w-4 h-4 ml-1" /></Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* STEP 3 - Instalaciones, DJ, extras */}
        {step === 3 && (
          <Card className="border-stone-200">
            <CardContent className="p-5 space-y-4">
              <h2 className="font-display text-xl text-stone-900 flex items-center gap-2"><Sparkles className="w-5 h-5 text-emerald-900" />Servicios y extras</h2>

              {config.instalaciones && (
                <div className="border border-stone-200 rounded-lg p-3 flex items-start justify-between gap-2" data-testid="card-instalaciones">
                  <div>
                    <p className="font-medium text-stone-900 flex items-center gap-1"><Building2 className="w-4 h-4" />{config.instalaciones.nombre}</p>
                    <p className="text-xs text-stone-500">{config.instalaciones.descripcion}</p>
                    <p className="text-sm font-semibold text-emerald-700 mt-1">{fmt(config.instalaciones.precio)} fijo</p>
                  </div>
                  <Checkbox checked={form.incluye_instalaciones} onCheckedChange={(v) => update({ incluye_instalaciones: !!v })} data-testid="check-instalaciones" />
                </div>
              )}

              {config.dj_base && (
                <div className="border border-stone-200 rounded-lg p-3 space-y-2" data-testid="card-dj">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-medium text-stone-900 flex items-center gap-1"><Music className="w-4 h-4" />{config.dj_base.nombre}</p>
                      <p className="text-sm font-semibold text-emerald-700 mt-1">{fmt(config.dj_base.precio)} fijo</p>
                    </div>
                    <Checkbox checked={form.incluye_dj} onCheckedChange={(v) => update({ incluye_dj: !!v })} data-testid="check-dj" />
                  </div>
                  {form.incluye_dj && config.dj_hora_extra && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="flex-1 text-stone-600">Horas extra de DJ ({fmt(config.dj_hora_extra.precio)}/h)</span>
                      <Input type="number" min={0} value={form.horas_extra_dj} onChange={(e) => update({ horas_extra_dj: parseInt(e.target.value) || 0 })} className="w-20 border-stone-300" data-testid="input-horas-dj" />
                    </div>
                  )}
                </div>
              )}

              {config.salon_hora_extra && (
                <div className="border border-stone-200 rounded-lg p-3 flex items-center gap-2 text-sm">
                  <span className="flex-1">
                    <p className="font-medium text-stone-900">{config.salon_hora_extra.nombre}</p>
                    <p className="text-xs text-stone-500">{fmt(config.salon_hora_extra.precio)} por hora adicional</p>
                  </span>
                  <Input type="number" min={0} value={form.horas_extra_local} onChange={(e) => update({ horas_extra_local: parseInt(e.target.value) || 0 })} className="w-20 border-stone-300" data-testid="input-horas-salon" />
                </div>
              )}

              {config.extras.length > 0 && (
                <div>
                  <Label className="mb-2 block">Extras disponibles</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {config.extras.filter(e => e.precio > 0).map(e => {
                      const selected = isExtraSelected(e.servicio_id);
                      const item = form.extras.find(x => x.servicio_id === e.servicio_id);
                      const needsQty = ["por_hora", "por_unidad"].includes(e.cobro_tipo);
                      return (
                        <div key={e.servicio_id} className={`border rounded-lg p-3 ${selected ? "border-emerald-700 bg-emerald-50" : "border-stone-200"}`}>
                          <div className="flex items-start justify-between gap-2">
                            <button type="button" onClick={() => toggleExtra(e.servicio_id)} className="flex-1 text-left" data-testid={`extra-${e.servicio_id}`}>
                              <p className="font-medium text-stone-900">{e.nombre}</p>
                              <p className="text-xs text-stone-500">{e.descripcion}</p>
                              <p className="text-sm font-semibold text-emerald-700 mt-1">
                                {fmt(e.precio)} {e.cobro_tipo !== "fijo" && `(${e.cobro_tipo.replace("_", " ")})`}
                              </p>
                            </button>
                            <Checkbox checked={selected} onCheckedChange={() => toggleExtra(e.servicio_id)} />
                          </div>
                          {selected && needsQty && (
                            <div className="mt-2 flex items-center gap-2 text-sm">
                              <span className="text-stone-500">Cantidad:</span>
                              <Input type="number" min={1} value={item?.cantidad || 1} onChange={(ev) => updateExtraQty(e.servicio_id, parseInt(ev.target.value) || 1)} className="w-20 border-stone-300" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <Button onClick={() => setStep(2)} variant="outline" className="flex-1 border-stone-300"><ChevronLeft className="w-4 h-4 mr-1" />Atrás</Button>
                <Button onClick={() => setStep(4)} className="flex-1 bg-emerald-900 hover:bg-emerald-800 text-white" data-testid="btn-step3-next">Ver resumen <ChevronRight className="w-4 h-4 ml-1" /></Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* STEP 4 - Resumen y envio (nunca en blanco: siempre muestra header, estado o resumen) */}
        {step === 4 && (
          <Card className="border-stone-200" data-testid="cotizador-step4">
            <CardContent className="p-5 space-y-4">
              <h2 className="font-display text-xl text-stone-900">Tu cotización</h2>

              {/* Estado: calculando */}
              {calculating && !calc && (
                <div className="flex flex-col items-center justify-center py-10 text-center" data-testid="step4-loading">
                  <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin mb-3" />
                  <p className="text-sm text-stone-600">Calculando tu cotización…</p>
                </div>
              )}

              {/* Estado: error al calcular */}
              {calcError && !calc && !calculating && (
                <div className="bg-red-50 border border-red-200 rounded p-4 text-sm text-red-800 space-y-3" data-testid="step4-error">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{calcError}</span>
                  </div>
                  <Button onClick={() => calcular(form)} size="sm" variant="outline" className="border-red-300" data-testid="btn-recalcular">
                    Reintentar cálculo
                  </Button>
                </div>
              )}

              {/* Estado: faltan datos mínimos (sin menú seleccionado) */}
              {!calc && !calculating && !calcError && (
                <div className="bg-amber-50 border border-amber-200 rounded p-4 text-sm text-amber-800 space-y-3" data-testid="step4-incompleto">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>Faltan datos para calcular tu cotización. Regresa al paso 2 y selecciona un menú principal.</span>
                  </div>
                  <Button onClick={() => setStep(2)} size="sm" variant="outline" className="border-amber-300" data-testid="btn-ir-paso2">
                    Ir al paso 2
                  </Button>
                </div>
              )}

              {/* Resumen normal */}
              {calc && (
              <div className="bg-stone-50 rounded-lg p-4 space-y-2 text-sm">
                {calc.subtotal_instalaciones > 0 && (
                  <div className="flex justify-between"><span>Uso de instalaciones</span><span className="font-medium">{fmt(calc.subtotal_instalaciones)}</span></div>
                )}
                {calc.instalaciones_incluidas_en_paquete && (
                  <div className="flex justify-between text-emerald-700"><span>Uso de instalaciones</span><span className="font-medium italic text-xs">Incluido en el paquete</span></div>
                )}
                {calc.menu_adulto && form.adultos > 0 && (
                  <div className="flex justify-between"><span>{form.adultos} adultos · {calc.menu_adulto.nombre}</span><span className="font-medium">{fmt(calc.subtotal_menu_adultos)}</span></div>
                )}
                {calc.menu_nino && form.ninos > 0 && (
                  <div className="flex justify-between"><span>{form.ninos} niños · {calc.menu_nino.nombre}</span><span className="font-medium">{fmt(calc.subtotal_menu_ninos)}</span></div>
                )}
                {calc.personas_faltantes > 0 && (
                  <div className="flex justify-between text-amber-700"><span>Ajuste mínimo operativo ({calc.personas_faltantes} personas)</span><span className="font-medium">{fmt(calc.subtotal_minimo)}</span></div>
                )}
                {calc.total_dj > 0 && (
                  <div className="flex justify-between"><span>DJ {form.horas_extra_dj > 0 && `+ ${form.horas_extra_dj}h extra`}</span><span className="font-medium">{fmt(calc.total_dj)}</span></div>
                )}
                {calc.total_horas_extra_local > 0 && (
                  <div className="flex justify-between"><span>{form.horas_extra_local}h extra de salón</span><span className="font-medium">{fmt(calc.total_horas_extra_local)}</span></div>
                )}
                {(calc.extras_detalle || []).map(e => (
                  <div key={e.servicio_id} className="flex justify-between"><span>{e.nombre} {e.cantidad > 1 && `x${e.cantidad}`}</span><span className="font-medium">{fmt(e.importe)}</span></div>
                ))}
                <hr className="border-stone-300 my-2" />
                <div className="flex justify-between text-stone-600"><span>Subtotal</span><span>{fmt(calc.subtotal_general)}</span></div>
                {calc.iva_activo && calc.iva_monto > 0 && (
                  <div className="flex justify-between text-stone-600"><span>IVA ({calc.iva_porcentaje}%)</span><span>{fmt(calc.iva_monto)}</span></div>
                )}
                <div className="flex justify-between text-lg font-bold text-emerald-900 pt-1"><span>Total</span><span data-testid="resumen-total">{fmt(calc.total)}</span></div>
                <div className="flex justify-between text-sm pt-2 border-t border-stone-200"><span>Anticipo para apartar fecha</span><span className="font-semibold text-emerald-700">{fmt(calc.anticipo)}</span></div>
                <div className="flex justify-between text-sm"><span>Saldo pendiente</span><span className="font-semibold">{fmt(calc.saldo)}</span></div>
              </div>
              )}
              {calc?.condiciones && <p className="text-xs text-stone-500 italic">{calc.condiciones}</p>}
              <div className="flex gap-2">
                <Button onClick={() => setStep(3)} variant="outline" className="flex-1 border-stone-300" data-testid="btn-step4-atras"><ChevronLeft className="w-4 h-4 mr-1" />Editar</Button>
                <Button onClick={enviar} disabled={sending || calculating || !calc} className="flex-1 bg-emerald-900 hover:bg-emerald-800 text-white" data-testid="btn-enviar-cotizacion">
                  <Send className="w-4 h-4 mr-1" />{sending ? "Enviando..." : "Enviar solicitud"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CotizadorPublico;
